import { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Layout } from '../components/layout/Layout';
import { investmentsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Bitcoin, Loader2, BookmarkPlus, ShieldCheck, History } from 'lucide-react';
import { INVESTMENT_STATUS, CRYPTO_OPTIONS, fmtDate, fmtEur, InvestmentStatusBadge, CryptoChip } from '../components/investment/investmentShared';
import { InvestmentPaymentWallet } from '../components/investment/InvestmentPaymentWallet';
import { InvestmentOpportunitiesHero } from '../components/investment/InvestmentOpportunitiesHero';

const QUICK_AMOUNTS = [500, 1000, 2500, 5000];

const InvestmentPage = () => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [sending, setSending] = useState(false);
    const [amount, setAmount] = useState('');
    const [crypto, setCrypto] = useState('BTC');
    const [note, setNote] = useState('');
    const [opportunity, setOpportunity] = useState(null);
    const formRef = useRef(null);
    const amountRef = useRef(null);
    const location = useLocation();

    const goToForm = () => {
        formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        setTimeout(() => amountRef.current?.focus(), 450);
    };
    const selectOpportunity = (opp) => {
        setOpportunity(opp);
        setCrypto(opp.asset);
        if (opp.min_amount && !amount) setAmount(String(opp.min_amount));
        goToForm();
    };
    useEffect(() => { if (location.hash === '#apartar') setTimeout(goToForm, 300); }, [location.hash]);
    useEffect(() => {
        const oppId = new URLSearchParams(location.search).get('opp');
        if (!oppId) return;
        investmentsAPI.opportunities().then(({ data }) => {
            const found = (data.opportunities || []).find((o) => o.id === oppId);
            if (found) selectOpportunity(found);
        }).catch(() => {});
    }, [location.search]); // eslint-disable-line react-hooks/exhaustive-deps

    const load = async () => {
        try {
            const { data } = await investmentsAPI.mine();
            setItems(data.requests || []);
        } catch {
            toast.error('Error al cargar tus solicitudes de inversión');
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const submit = async () => {
        const amt = parseFloat(amount);
        if (!amt || amt <= 0) { toast.error('Indica el importe que deseas apartar'); return; }
        setSending(true);
        try {
            await investmentsAPI.reserve({ amount: amt, crypto, note, opportunity_id: opportunity?.id || null });
            toast.success('Inversión apartada. Tu solicitud queda en estado Pendiente.');
            setAmount(''); setNote(''); setOpportunity(null);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Error al apartar la inversión');
        } finally {
            setSending(false);
        }
    };

    const counts = items.reduce((a, r) => ({ ...a, [r.status]: (a[r.status] || 0) + 1 }), {});

    return (
        <Layout>
            <div className="max-w-5xl mx-auto space-y-6" data-testid="investment-page">
                <InvestmentOpportunitiesHero onCta={goToForm} onSelectOpportunity={selectOpportunity} />
                <div className="flex items-center gap-3">
                    <div className="flex items-center justify-center w-12 h-12 rounded-2xl bg-amber-500/15 border border-amber-500/30">
                        <Bitcoin className="w-6 h-6 text-amber-300" />
                    </div>
                    <div>
                        <h1 className="text-2xl sm:text-3xl font-bold text-white tracking-tight uppercase">Inversión</h1>
                        <p className="text-sm text-slate-400 font-light">Aparta el importe que deseas invertir en BTC o USDT TRC20 · Te confirmaremos la reserva</p>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
                    <div ref={formRef} id="apartar" className="lg:col-span-3 rounded-2xl bg-slate-900/70 border border-slate-800 p-5 space-y-5 scroll-mt-6" data-testid="investment-form-card">
                        <p className="text-white font-semibold text-sm flex items-center gap-2"><BookmarkPlus className="w-4 h-4 text-amber-300" /> Nueva reserva</p>
                        {opportunity && (
                            <div className="flex items-center justify-between gap-2 rounded-lg border border-[#d4af37]/40 bg-[#d4af37]/10 px-3 py-2" data-testid="investment-selected-opportunity">
                                <p className="text-xs text-[#f3e3a6]">Oportunidad: <span className="font-semibold text-white">{opportunity.name}</span></p>
                                <button type="button" onClick={() => setOpportunity(null)} className="text-[11px] text-[#a3a3ab] hover:text-white" data-testid="investment-clear-opportunity">Quitar</button>
                            </div>
                        )}

                        <div className="space-y-2">
                            <Label className="text-slate-400 text-xs">Criptomoneda</Label>
                            <div className="grid grid-cols-2 gap-3">
                                {CRYPTO_OPTIONS.map((c) => (
                                    <button key={c.key} type="button" onClick={() => setCrypto(c.key)}
                                        className={`rounded-xl border px-4 py-3 text-left transition-all ${crypto === c.key ? 'border-amber-400/70 bg-amber-500/10 shadow-[0_0_0_1px_rgba(251,191,36,0.3)]' : 'border-slate-800 bg-slate-950 hover:border-slate-600'}`}
                                        data-testid={`investment-crypto-${c.key}`} aria-pressed={crypto === c.key}>
                                        <div className="flex items-center gap-2">
                                            <span className="w-2.5 h-2.5 rounded-full" style={{ background: c.color }} />
                                            <span className="text-white text-sm font-semibold">{c.label}</span>
                                        </div>
                                        <p className="text-slate-500 text-[11px] mt-0.5">{c.sub}</p>
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label className="text-slate-400 text-xs">Importe deseado (EUR)</Label>
                            <Input ref={amountRef} type="number" min="0.01" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0,00"
                                className="bg-slate-950 border-slate-800 text-white text-lg h-12" data-testid="investment-amount-input" />
                            <div className="flex flex-wrap gap-2">
                                {QUICK_AMOUNTS.map((q) => (
                                    <button key={q} type="button" onClick={() => setAmount(String(q))}
                                        className="px-3 py-1 rounded-full border border-slate-700 text-xs text-slate-300 hover:border-amber-400/60 hover:text-white transition-colors"
                                        data-testid={`investment-quick-${q}`}>{fmtEur(q)}</button>
                                ))}
                            </div>
                        </div>

                        <div className="space-y-1.5">
                            <Label className="text-slate-400 text-xs">Comentario (opcional)</Label>
                            <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Ej: Prefiero entrada escalonada"
                                className="bg-slate-950 border-slate-800 text-white text-sm" data-testid="investment-note-input" />
                        </div>

                        <Button onClick={submit} disabled={sending || !amount}
                            className="w-full h-11 bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold" data-testid="investment-reserve-btn">
                            {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <BookmarkPlus className="w-4 h-4 mr-2" />}
                            Apartar inversión
                        </Button>
                        <p className="text-[11px] text-slate-500 flex items-start gap-1.5">
                            <ShieldCheck className="w-3.5 h-3.5 text-emerald-400 mt-0.5 flex-shrink-0" />
                            Apartar no mueve tu saldo. Un asesor revisará tu solicitud y la marcará como Reservada o Disponible próximamente.
                        </p>
                    </div>

                    <div className="lg:col-span-2 space-y-3">
                        {Object.entries(INVESTMENT_STATUS).map(([key, st]) => (
                            <div key={key} className="rounded-2xl bg-slate-900/70 border border-slate-800 px-4 py-3 flex items-center gap-3" data-testid={`investment-summary-${key}`}>
                                <div className={`w-9 h-9 rounded-xl border flex items-center justify-center ${st.cls}`}><st.Icon className="w-4 h-4" /></div>
                                <div className="flex-1">
                                    <p className="text-slate-400 text-xs">{st.label}</p>
                                    <p className="text-white text-xl font-bold">{counts[key] || 0}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="rounded-2xl bg-slate-900/70 border border-slate-800 overflow-hidden">
                    <div className="px-4 py-3 border-b border-slate-800 flex items-center gap-2">
                        <History className="w-4 h-4 text-slate-400" />
                        <p className="text-white font-semibold text-sm">Historial de solicitudes</p>
                    </div>
                    {loading ? (
                        <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 text-amber-300 animate-spin" /></div>
                    ) : items.length === 0 ? (
                        <p className="text-slate-600 text-sm text-center py-10" data-testid="investment-empty">Aún no has apartado ninguna inversión.</p>
                    ) : (
                        <div className="divide-y divide-slate-800/60">
                            {items.map((r) => (
                                <div key={r.id} className="px-4 py-3 flex items-start gap-3" data-testid={`investment-item-${r.id}`}>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-white text-sm font-semibold">{fmtEur(r.amount)}</p>
                                        <div className="flex items-center gap-2 mt-0.5"><CryptoChip crypto={r.crypto} /><span className="text-slate-600 text-[11px]">· {fmtDate(r.created_at)}</span></div>
                                        {r.opportunity_name && <p className="text-[#f3e3a6] text-xs mt-0.5" data-testid={`investment-opportunity-${r.id}`}>Oportunidad: {r.opportunity_name}</p>}
                                        {r.note && <p className="text-slate-400 text-xs mt-0.5">Tu comentario: {r.note}</p>}
                                        {r.admin_note && (
                                            <p className="text-amber-200 text-xs mt-1 bg-amber-500/10 border border-amber-500/25 rounded-lg px-2.5 py-1.5" data-testid={`investment-admin-note-${r.id}`}>
                                                Nota del asesor: {r.admin_note}
                                            </p>
                                        )}
                                        {r.status === 'reserved' && <InvestmentPaymentWallet request={r} onChanged={load} />}
                                    </div>
                                    <InvestmentStatusBadge status={r.status} testId={`investment-status-${r.id}`} />
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </Layout>
    );
};

export default InvestmentPage;
